import { TranslateService } from '@ngx-translate/core';
import { SubHeaderService } from '../themes/services/subheader.service';
import { takeUntil, Subject } from 'rxjs';
import { PaginationOptions } from './models/filter.model';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppInjector } from '@uw/app.injector';
import { ErrorService } from './services/error.service';
import {
  LayoutUtilsService,
  MessageType,
} from '@uw/shared/services/layout-utils.service';
import { ApplicationPermissions } from './application-permission';
import { UwTranslationService as UwTranslationService } from './services/uw-translation-service.service';

@Component({ template: '', host: { 'data-component': 'base' } })
export class BaseComponent implements OnInit, OnDestroy {
  destroy$ = new Subject();
  router!: Router;
  applicationPermissionEnum = ApplicationPermissions;
  activatedRoute: ActivatedRoute;
  subHeaderService: SubHeaderService;
  pagination: PaginationOptions = { page: 0, limit: 10 };
  errorService: ErrorService;
  public layoutUtilsService: LayoutUtilsService;
  public translateService: TranslateService;
  private _uwTranslateService: UwTranslationService;

  constructor() {
    const injector = AppInjector.getInjector();
    this.router = injector.get(Router);
    this.subHeaderService = injector.get(SubHeaderService);
    this.activatedRoute = injector.get(ActivatedRoute);
    this.errorService = injector.get(ErrorService);
    this.layoutUtilsService = injector.get(LayoutUtilsService);
    this.translateService = injector.get(TranslateService);
    this._uwTranslateService = injector.get(UwTranslationService);
  }

  ngOnInit(): void {
    this.errorService.resetError();
    this.subHeaderService.setSubTitle('');
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((queryParams) => {
        this.parsePaginationParams(queryParams);
      });
  }

  set subHeaderTitle(title: string) {
    this.subHeaderService.setTitle(title);
  }

  set subHeaderBadge(badge: string | null) {
    this.subHeaderService.setBadge(badge);
  }

  /**
   * @deprecated Since release 6.3. Use subHeaderSubTitleWithParams() instead
   */
  set subHeaderSubTitle(subTitle: string) {
    this.subHeaderService.setSubTitle(
      subTitle ? this.translateService.instant(subTitle) : subTitle
    );
  }

  set subHeaderSubTitleWithParams(metadata: {key: string, params?: {[key: string]: string}}) {
    this.subHeaderService.setSubTitleWithTranslateParams(metadata.key, metadata.params);
  }

  set setPageError(errorMessage: string) {
    this.errorService.setError(this.translateService.instant(errorMessage));
  }

  resetErrors() {
    this.errorService.resetError();
  }

   /**
   * 
   * @deprecated Since 6.3 Release. Use showTranslatedErrorMessage() instead.
   */
  showError(message: string) {
    this.layoutUtilsService.showNotification(
      this.translateService.instant(message),
      MessageType.Error
    );
  }

  showTranslatedErrorMessage(messageKey: string, translationParams?: {[key: string]: string}) {
    const message = this._uwTranslateService.translateWithParams(messageKey, translationParams)

    this.layoutUtilsService.showNotification(
      this.translateService.instant(message),
      MessageType.Error
    );
  }

  showErrorMessageInDialog(message: string) {
    this.layoutUtilsService.showErrorMessageDialogElement(
      'common.error',
      message
    );
  }

   /**
   * 
   * @deprecated Since 6.3 Release. Use showTranslatedSuccessMessage() instead.
   */
  showSuccess(message: string) {
    this.layoutUtilsService.showNotification(
      this.translateService.instant(message),
      MessageType.Success
    );
  }

  showTranslatedSuccessMessage(messageKey: string, translationParams?: {[key: string]: string}) {
    const message = this._uwTranslateService.translateWithParams(messageKey, translationParams)
    this.layoutUtilsService.showNotification(
      this.translateService.instant(message),
      MessageType.Success
    );
  }

  updatePaginationQueryParameters(): void {
    this.router.navigate([], {
      queryParams: {
        ...this.pagination,
      },
      queryParamsHandling: 'merge',
      relativeTo: this.activatedRoute,
      replaceUrl: true,
    });
  }

  ngOnDestroy(): void {
    this.subHeaderTitle = '';
    this.subHeaderSubTitle = '';
    this.subHeaderBadge = null;
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private parsePaginationParams(params: Params) {
    if (params.page) {
      this.pagination.page = params.page ? parseFloat(params.page) : 0;
    }
    if (params.limit) {
      this.pagination.limit = params.limit ? parseFloat(params.limit) : 10;
    }
    if (params.sort) {
      this.pagination.sort = params.sort;
    }
    if (params.sortColumn) {
      this.pagination.sortColumn = params.sortColumn;
    }
  }

  downLoadFile(data: any, fileName: string = '') {
    const name = fileName !== '' ? fileName : data.filename;

    const element = document.createElement('a');

    //Other browsers
    element.href = URL.createObjectURL(data.image);
    element.download = name;
    document.body.appendChild(element);
    element.click();
  }
}
